/** @jsx jsx */
import { OpenAiLogo } from '@datacamp/waffles/asset';
import { Button } from '@datacamp/waffles/button';
import { hexToRgba } from '@datacamp/waffles/helpers';
import { Sparkles, Undo } from '@datacamp/waffles/icon';
import { tokens } from '@datacamp/waffles/tokens';
import { Tooltip } from '@datacamp/waffles/tooltip';
import { jsx, keyframes } from '@emotion/react';
import isNumber from 'lodash/isNumber';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import config from '../../config';
import MarkdownSubmitButton from '../../containers/MarkdownSubmitButton';
import { formatTitle } from '../../helpers/examUtilities';

const fadeIn = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

type Props = {
  canExplainSolution: boolean;
  canRun: boolean;
  canSubmit: boolean;
  ckey: string;
  disableExecution: boolean;
  disableSubmit?: boolean;
  epicResetCode: () => void;
  explainCodeButtonEnabled: boolean;
  hideOpenAIBranding: boolean;
  id: number;
  isBusy: boolean;
  isCompletedOverlayShown: boolean;
  isLightTheme: boolean;
  isMarkdownExercise?: boolean;
  isReady: boolean;
  isSolution?: boolean;
  nbAttempts?: number;
  onExplainSolution: () => void;
  onReset?: () => void;
  onRun: () => void;
  onSubmit: () => void;
  userId: number;
};

const ExerciseExecuteCode: React.FC<Props> = ({
  canExplainSolution,
  canRun,
  canSubmit,
  ckey,
  disableExecution = false,
  disableSubmit = false,
  epicResetCode,
  explainCodeButtonEnabled,
  hideOpenAIBranding,
  id,
  isBusy,
  isCompletedOverlayShown,
  isLightTheme,
  isMarkdownExercise = false,
  isReady,
  isSolution = false,
  nbAttempts,
  onExplainSolution,
  onReset,
  onRun,
  onSubmit,
  userId,
}) => {
  const { t } = useTranslation();
  const disabled = isCompletedOverlayShown || !isReady || disableExecution;
  const submitTitle = isSolution
    ? t('ExerciseExecuteCode.solutionPrimaryButton')
    : t('ExerciseExecuteCode.primaryButton');

  const backgroundColor = isLightTheme
    ? tokens.colors.white
    : tokens.colors.navy;
  const backgroundWhenExplain = hexToRgba(backgroundColor, 0.9);

  // Track explain solution button shown
  useEffect(() => {
    if (canExplainSolution && window.dataLayer) {
      const { appName } = config;

      window.dataLayer.push({
        event: 'heap_event',
        heap_event_name: 'Learn - Campus - Explain Solution Shown',
        heap_event_properties: {
          app_id: appName,
          event_category: 'learning',
          identity: userId,
          exercise_id: id,
        },
      });
    }
  }, [canExplainSolution, id, userId]);

  const explainButton = (
    <Button
      aria-label={t('ExerciseExecuteCode.explainButtonLabel')}
      variant="secondary"
      iconLeft={<Sparkles />}
      disabled={!explainCodeButtonEnabled}
      data-cy="explain-solution-button"
      inverted={!isLightTheme}
      onClick={onExplainSolution}
    >
      {t('ExerciseExecuteCode.explainButton')}
    </Button>
  );

  return (
    <div
      css={{
        position: 'absolute',
        height: 64,
        bottom: 0,
        left: 0,
        right: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingLeft: 12,
        paddingRight: 12,
        pointerEvents: 'none',
        background: canExplainSolution ? backgroundWhenExplain : 'transparent',
        borderRadius: tokens.borderRadius.medium,
      }}
    >
      <div
        css={{
          pointerEvents: 'all',
          display: 'flex',
          gap: tokens.spacing.small,
          borderRadius: tokens.borderRadius.medium,
          background: 'transparent',
          opacity: 0,
          animationName: fadeIn,
          animationDuration: '200ms',
          animationDelay: '800ms',
          animationFillMode: 'forwards',
          animationTimingFunction: 'linear',
        }}
      >
        {canExplainSolution && (
          <div
            css={{
              display: 'inline-grid',
              gridTemplateColumns: 'auto auto',
              gap: tokens.spacing.medium,
            }}
          >
            {
              // We conditionally render the explain button with a tooltip if it's enabled
              // to fix an issue where the tooltip stays on the screen after the button is disabled
              explainCodeButtonEnabled ? (
                <Tooltip
                  content={t('ExerciseExecuteCode.explainButtonTooltip')}
                  placement="top"
                  inverted={!isLightTheme}
                >
                  {explainButton}
                </Tooltip>
              ) : (
                explainButton
              )
            }
            {!hideOpenAIBranding && (
              <div
                css={{
                  fontSize: tokens.fontSizes.small,
                  color: isLightTheme
                    ? tokens.colors.navySubtleTextOnLight
                    : tokens.colors.navySubtleTextOnDark,
                  alignSelf: 'center',
                }}
              >
                <span>
                  Powered by <OpenAiLogo width={58} height={14} />
                </span>
              </div>
            )}
          </div>
        )}
      </div>
      <div
        css={{
          pointerEvents: 'all',
          display: 'flex',
          gap: tokens.spacing.small,
          borderRadius: tokens.borderRadius.medium,
          backgroundColor,
        }}
      >
        {onReset && (
          <Tooltip
            content={t('ExerciseExecuteCode.resetButton')}
            placement="top"
            inverted={!isLightTheme}
          >
            <Button
              aria-label={t('ExerciseExecuteCode.resetButton')}
              variant="secondary"
              icon={<Undo />}
              data-cy="reset-code-button"
              inverted={!isLightTheme}
              onClick={() => {
                onReset();
                epicResetCode();
              }}
            />
          </Tooltip>
        )}
        {canRun && (
          <Tooltip
            content={t('ExerciseExecuteCode.runCodeTooltip')}
            placement="top"
            inverted={!isLightTheme}
          >
            <Button
              variant="secondary"
              onClick={onRun}
              data-cy="run-code-button"
              isLoading={isBusy}
              disabled={disabled}
              inverted={!isLightTheme}
            >
              {isNumber(nbAttempts)
                ? t('ExerciseExecuteCode.tryCodeButton')
                : t('ExerciseExecuteCode.runCodeButton')}
            </Button>
          </Tooltip>
        )}
        {canSubmit && (
          <Tooltip
            content="Ctrl + Shift + Enter"
            placement="topRight"
            inverted={!isLightTheme}
          >
            <Button
              variant="primary"
              onClick={onSubmit}
              data-cy="submit-button"
              data-testid="execute-code-button"
              isLoading={isBusy}
              disabled={disabled || disableSubmit}
              inverted={!isLightTheme}
            >
              {!isSolution && isNumber(nbAttempts)
                ? formatTitle(nbAttempts, submitTitle)
                : submitTitle}
            </Button>
          </Tooltip>
        )}
        {isMarkdownExercise && (
          <MarkdownSubmitButton
            loading={isBusy}
            onSubmit={onSubmit}
            ckey={ckey}
          />
        )}
      </div>
    </div>
  );
};

export default ExerciseExecuteCode;
